// Which practice sections a RECORDS-ONLY person may open (MEET R1, 2026-09-29).
//
// One row per section key, both verticals. A records-only person (grant
// `customer_records`) does the practice's paperwork: setup, files, answers,
// what is still needed, renewals. They never see a verdict, a finding, a
// count of findings, a report or a law-change impact. The server refuses
// those reads whatever this file says (`dashboard/deps.py`,
// `tests/test_records_only_access_db.py` walks every route). This registry
// keeps the SHELL honest too: no rail stop, landing or link opens a page the
// person cannot read, so they never see an empty results page or a dead link.
//
// A lane that adds a practice section adds ONE line here with `records:
// 'allowed' | 'hidden'`. A key that is not listed is hidden (fail closed).
// The server's copy of the allowed invitation destinations
// (`dashboard/services/invitation_target.py` RECORDS_*_SECTIONS) is held
// equal to this file by a test.

export type RecordsVisibility = 'allowed' | 'hidden';

export interface PracticeSectionEntry {
  id: string;
  records: RecordsVisibility;
}

export const TELEHEALTH_SECTIONS: PracticeSectionEntry[] = [
  { id: 'practice', records: 'allowed' },
  { id: 'prescribers', records: 'allowed' },
  { id: 'prescribing', records: 'allowed' },
  { id: 'agreements', records: 'allowed' },
  { id: 'need', records: 'allowed' },
  { id: 'stand', records: 'hidden' },
  { id: 'plan', records: 'hidden' },
  { id: 'cases', records: 'hidden' },
  { id: 'campaigns', records: 'allowed' },
  { id: 'notchecked', records: 'hidden' },
  { id: 'reports', records: 'hidden' },
  { id: 'changes', records: 'hidden' },
  { id: 'law', records: 'hidden' },
  { id: 'calendar', records: 'allowed' },
  { id: 'renewals', records: 'allowed' },
  { id: 'files', records: 'allowed' },
  { id: 'summary', records: 'hidden' },
  { id: 'package', records: 'hidden' },
  { id: 'access', records: 'hidden' },
];

export const MEDSPA_SECTIONS: PracticeSectionEntry[] = [
  { id: 'practice', records: 'allowed' },
  { id: 'people', records: 'allowed' },
  { id: 'places', records: 'allowed' },
  { id: 'services', records: 'allowed' },
  { id: 'team', records: 'allowed' },
  { id: 'questions', records: 'allowed' },
  { id: 'eligibility', records: 'hidden' },
  { id: 'compliance', records: 'hidden' },
  { id: 'positions', records: 'hidden' },
  { id: 'plan', records: 'hidden' },
  { id: 'cases', records: 'hidden' },
  { id: 'campaigns', records: 'allowed' },
  { id: 'lawchanges', records: 'hidden' },
  { id: 'calendar', records: 'allowed' },
  { id: 'journal', records: 'hidden' },
  { id: 'research', records: 'hidden' },
  { id: 'rollout', records: 'hidden' },
  { id: 'access', records: 'hidden' },
  { id: 'renewals', records: 'allowed' },
  { id: 'menu', records: 'hidden' },
  { id: 'files', records: 'allowed' },
  { id: 'summary', records: 'hidden' },
  { id: 'protocols', records: 'hidden' },
  { id: 'package', records: 'hidden' },
  { id: 'oversight', records: 'hidden' },
  { id: 'routing', records: 'hidden' },
];

export type PracticeVertical = 'telehealth' | 'medspa';

/** Where a records-only person lands: the still-needed list. */
export const RECORDS_HOME: Record<PracticeVertical, string> = {
  telehealth: 'need',
  medspa: 'questions',
};

const TABLE: Record<PracticeVertical, PracticeSectionEntry[]> = {
  telehealth: TELEHEALTH_SECTIONS,
  medspa: MEDSPA_SECTIONS,
};

/** Whether a section is open to this viewer. Full-access viewers see
 *  everything the shell already shows them; a records-only viewer sees only
 *  the rows marked `allowed`, and an unlisted key is hidden. */
export function sectionVisible(
  vertical: PracticeVertical,
  id: string,
  recordsOnly: boolean,
): boolean {
  if (!recordsOnly) return true;
  return TABLE[vertical].some((entry) => entry.id === id && entry.records === 'allowed');
}

/** The section to render for a records-only viewer: the one asked for when it
 *  is open to them, else their home. Never a page they cannot read. */
export function recordsSection<T extends string>(
  vertical: PracticeVertical,
  requested: T | null,
  recordsOnly: boolean,
): T | null {
  if (!recordsOnly) return requested;
  if (requested && sectionVisible(vertical, requested, true)) return requested;
  return RECORDS_HOME[vertical] as T;
}

// SCOPEDACCESS (SkinSpirit S3): the med-spa sections a NARROWED person opens --
// someone who sees only some locations or states, or can view only. The
// server refuses every other route to them (`dashboard/services/
// practice_scope.py`); this keeps the shell honest the same way the records
// table does. The server's copy (`invitation_target.NARROWED_MEDSPA_SECTIONS`)
// is held equal to this list by a test.
export const NARROWED_MEDSPA_SECTIONS: readonly string[] = [
  'compliance',
  'questions',
  'eligibility',
  'lawchanges',
];
const NARROWED_HOME = 'compliance';

export function narrowedSection<T extends string>(requested: T | null): T {
  if (requested && NARROWED_MEDSPA_SECTIONS.includes(requested)) return requested;
  return NARROWED_HOME as T;
}
